/* ---------------------------------------------------------------------------
   Web Mercator, the two directions, and the tile arithmetic on top of it.

   Everything the map draws — tiles, the bundled outline, the markers — has to
   agree about where a coordinate lands on screen, or the outline sits beside the
   country instead of on it. So the projection lives here, on its own, with no DOM
   in sight: one pair of functions, one scale, and `npm test` checks the round
   trip rather than trusting the algebra.

   EPSG:3857, the projection every slippy-map tile server uses. World pixel space
   is 256 × 2^zoom square, x running west to east from 180° W, y running north to
   south from about 85.05° N — y is DOWN, which is what a screen wants and the
   opposite of what latitude does.
   --------------------------------------------------------------------------- */

/** Tile edge in CSS pixels. 256 is the size every OSM tile is served at. */
export const TILE = 256;

/**
 * Latitude beyond which Mercator runs away to infinity.
 *
 * The projection stretches without bound at the poles, so every implementation
 * clamps; this is the conventional cut, and it is what makes the world square.
 */
const MAX_LAT = 85.0511287798066;

/** World pixel width (and height) at this zoom. */
export function worldSize(zoom: number): number {
  return TILE * 2 ** zoom;
}

/** A point in world pixel space at a given zoom. */
export interface Point {
  x: number;
  y: number;
}

/** Longitude/latitude in degrees to world pixels. */
export function project(lng: number, lat: number, zoom: number): Point {
  const size = worldSize(zoom);
  const clamped = Math.max(-MAX_LAT, Math.min(MAX_LAT, lat));
  const sin = Math.sin((clamped * Math.PI) / 180);
  return {
    x: ((lng + 180) / 360) * size,
    // The log term is Mercator's y; the 0.5 − …/(4π) form maps it into 0..1
    // north-to-south, which is then scaled to the world square.
    y: (0.5 - Math.log((1 + sin) / (1 - sin)) / (4 * Math.PI)) * size,
  };
}

/** World pixels back to longitude/latitude in degrees. */
export function unproject(x: number, y: number, zoom: number): { lng: number; lat: number } {
  const size = worldSize(zoom);
  const n = Math.PI * (1 - 2 * (y / size));
  return {
    lng: (x / size) * 360 - 180,
    lat: (180 / Math.PI) * Math.atan(Math.sinh(n)),
  };
}

/** A rectangle of longitude/latitude, as the register's bounds are written. */
export interface Bounds {
  minLat: number;
  maxLat: number;
  minLng: number;
  maxLng: number;
}

/**
 * The view that fits `bounds` into a `width` × `height` box.
 *
 * Returns a fractional zoom deliberately — the tile layer floors it to pick a
 * tile level and scales the difference, which is how a map fits an arbitrary box
 * without jumping to the nearest power of two.
 *
 * `padding` is in pixels and applies to all four sides: a fit with none puts the
 * outermost feature exactly on the edge, where a marker is half cut off.
 */
export function fitBounds(
  bounds: Bounds,
  width: number,
  height: number,
  padding = 24,
): { lat: number; lng: number; zoom: number } {
  const w = Math.max(1, width - padding * 2);
  const h = Math.max(1, height - padding * 2);

  /* Measured at zoom 0 and converted to a zoom afterwards. The span in world
     pixels doubles per level, so the zoom that fits is log2 of the ratio — and
     because that is exact for both axes, the smaller of the two is the one that
     fits both. */
  const a = project(bounds.minLng, bounds.maxLat, 0);
  const b = project(bounds.maxLng, bounds.minLat, 0);
  const spanX = Math.max(1e-9, b.x - a.x);
  const spanY = Math.max(1e-9, b.y - a.y);

  return {
    lng: (bounds.minLng + bounds.maxLng) / 2,
    // NOT the mean of the two latitudes: the centre of the box on screen is the
    // centre in PROJECTED space, and Mercator's y is not linear in latitude.
    lat: unproject(0, (a.y + b.y) / 2, 0).lat,
    zoom: Math.log2(Math.min(w / spanX, h / spanY)),
  };
}

/** The bounds that contain every point given, or null if there are none. */
export function boundsOf(points: readonly { lat: number; lng: number }[]): Bounds | null {
  if (points.length === 0) return null;
  let minLat = 90;
  let maxLat = -90;
  let minLng = 180;
  let maxLng = -180;
  for (const p of points) {
    minLat = Math.min(minLat, p.lat);
    maxLat = Math.max(maxLat, p.lat);
    minLng = Math.min(minLng, p.lng);
    maxLng = Math.max(maxLng, p.lng);
  }
  return { minLat, maxLat, minLng, maxLng };
}

/**
 * Grow bounds that are too small to fit sensibly.
 *
 * Five structures within a few kilometres of each other produce a span of almost
 * nothing, and a fit to that asks for zoom 19 over open country. `min` is in
 * degrees and is applied about the centre of whichever axis is short.
 */
export function padBounds(bounds: Bounds, min: number): Bounds {
  const growLat = Math.max(0, min - (bounds.maxLat - bounds.minLat)) / 2;
  const growLng = Math.max(0, min - (bounds.maxLng - bounds.minLng)) / 2;
  return {
    minLat: bounds.minLat - growLat,
    maxLat: bounds.maxLat + growLat,
    minLng: bounds.minLng - growLng,
    maxLng: bounds.maxLng + growLng,
  };
}

/** One tile of the grid, at a zoom level. */
export interface Tile {
  x: number;
  y: number;
  z: number;
  /** Where its top-left corner goes, in the layer's own pixel space. */
  left: number;
  top: number;
}

/**
 * Every tile needed to cover a `width` × `height` viewport centred on a point.
 *
 * `z` is the integer tile level; the caller is responsible for scaling if its
 * view zoom is fractional. Tiles outside the grid are dropped rather than
 * requested — north and south of the world there is nothing to fetch — while x
 * WRAPS, because the world does.
 */
export function tilesFor(
  centre: { lat: number; lng: number },
  z: number,
  width: number,
  height: number,
): { tiles: Tile[]; originX: number; originY: number } {
  const c = project(centre.lng, centre.lat, z);
  // Top-left of the viewport in world pixels. Everything below is relative to
  // this, so the layer's own coordinates start at 0 whatever the centre is.
  const originX = c.x - width / 2;
  const originY = c.y - height / 2;

  const first = Math.floor(originX / TILE);
  const last = Math.floor((originX + width) / TILE);
  const top = Math.floor(originY / TILE);
  const bottom = Math.floor((originY + height) / TILE);
  const count = 2 ** z;

  const tiles: Tile[] = [];
  for (let ty = top; ty <= bottom; ty++) {
    if (ty < 0 || ty >= count) continue;
    for (let tx = first; tx <= last; tx++) {
      tiles.push({
        // Positive modulo: a viewport straddling the antimeridian asks for
        // negative x, and tile -1 is the easternmost column.
        x: ((tx % count) + count) % count,
        y: ty,
        z,
        left: tx * TILE - originX,
        top: ty * TILE - originY,
      });
    }
  }
  return { tiles, originX, originY };
}
